/**
 * ChallengeHub Page
 *
 * Lists all available challenges with filtering by difficulty.
 * Entry point to the Challenge Lab feature.
 */

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ChevronLeft, Target, Filter, History, Loader2 } from 'lucide-react'
import { Button, Card, CardContent } from '@/components/ui'
import { ChallengeCard } from '@/components/challenge'
import { useFamily } from '@/context/FamilyContext'
import { useToast } from '@/context/ToastContext'
import {
  getChallenges,
  getSubmissionsForChild,
  type ChallengeFilters,
} from '@/services/challengeService'
import type { StoryChallenge, ChallengeSubmission, ChallengeDifficulty } from '@/types/database'

const DIFFICULTY_OPTIONS: Array<{ value: ChallengeDifficulty | 'all'; label: string }> = [
  { value: 'all', label: 'All Levels' },
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
]

export function ChallengeHub() {
  const { activeWriter } = useFamily()
  const { showToast } = useToast()

  const [challenges, setChallenges] = useState<StoryChallenge[]>([])
  const [submissions, setSubmissions] = useState<ChallengeSubmission[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedDifficulty, setSelectedDifficulty] = useState<ChallengeDifficulty | 'all'>('all')

  // Load challenges and submissions
  useEffect(() => {
    async function loadData() {
      if (!activeWriter) return

      setIsLoading(true)
      try {
        const filters: ChallengeFilters = {}
        if (selectedDifficulty !== 'all') {
          filters.difficulty = selectedDifficulty
        }

        const [challengeData, submissionData] = await Promise.all([
          getChallenges(filters),
          getSubmissionsForChild(activeWriter.id),
        ])

        setChallenges(challengeData)
        setSubmissions(submissionData)
      } catch (error) {
        console.error('Error loading challenges:', error)
        showToast('Failed to load challenges', 'error')
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [activeWriter, selectedDifficulty, showToast])

  // Check if challenge was completed today
  const isCompletedToday = (challengeId: string): boolean => {
    const today = new Date().toISOString().split('T')[0]
    return submissions.some(
      s =>
        s.challenge_id === challengeId &&
        s.status === 'submitted' &&
        s.submission_date === today
    )
  }

  // Separate featured and regular challenges
  const featuredChallenges = challenges.filter(c => c.is_featured)
  const regularChallenges = challenges.filter(c => !c.is_featured)

  return (
    <div className="min-h-screen bg-[var(--color-kids-bg)]">
      {/* Header */}
      <header className="bg-[var(--color-kids-surface)] border-b border-[var(--color-kids-surface-alt)] sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm">
                  <ChevronLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <Target className="w-6 h-6 text-[var(--color-kids-secondary)]" />
                <h1 className="font-display font-bold text-xl text-[var(--color-kids-text)]">
                  Challenge Lab
                </h1>
              </div>
            </div>

            <Link to="/challenge/history">
              <Button variant="ghost" size="sm">
                <History className="w-5 h-5 mr-1" />
                History
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Intro */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <h2 className="font-display font-bold text-lg text-[var(--color-kids-text)] mb-2">
              Practice Makes Better Writers
            </h2>
            <p className="text-[var(--color-kids-text-muted)]">
              Pick a challenge, write your story, and get instant feedback on your story structure.
              The more you practice, the stronger your writing becomes!
            </p>
          </CardContent>
        </Card>

        {/* Filters */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
          <Filter className="w-4 h-4 text-[var(--color-kids-text-muted)] flex-shrink-0" />
          {DIFFICULTY_OPTIONS.map(option => (
            <button
              key={option.value}
              onClick={() => setSelectedDifficulty(option.value)}
              className={`
                px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors
                ${
                  selectedDifficulty === option.value
                    ? 'bg-[var(--color-kids-primary)] text-white'
                    : 'bg-[var(--color-kids-surface)] text-[var(--color-kids-text-muted)] hover:bg-[var(--color-kids-surface-alt)]'
                }
              `}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--color-kids-primary)]" />
          </div>
        )}

        {/* Challenges Grid */}
        {!isLoading && (
          <div className="space-y-8">
            {/* Featured Challenges */}
            {featuredChallenges.length > 0 && (
              <section>
                <h3 className="font-display font-bold text-lg text-[var(--color-kids-text)] mb-4">
                  Featured Challenges
                </h3>
                <div className="grid gap-4 md:grid-cols-2">
                  {featuredChallenges.map((challenge, index) => (
                    <motion.div
                      key={challenge.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <ChallengeCard
                        challenge={challenge}
                        completedToday={isCompletedToday(challenge.id)}
                      />
                    </motion.div>
                  ))}
                </div>
              </section>
            )}

            {/* All Challenges */}
            <section>
              <h3 className="font-display font-bold text-lg text-[var(--color-kids-text)] mb-4">
                {selectedDifficulty === 'all' ? 'All Challenges' : `${selectedDifficulty.charAt(0).toUpperCase() + selectedDifficulty.slice(1)} Challenges`}
              </h3>
              {regularChallenges.length > 0 ? (
                <div className="grid gap-4 md:grid-cols-2">
                  {regularChallenges.map((challenge, index) => (
                    <motion.div
                      key={challenge.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <ChallengeCard
                        challenge={challenge}
                        completedToday={isCompletedToday(challenge.id)}
                      />
                    </motion.div>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Target className="w-12 h-12 mx-auto mb-3 text-[var(--color-kids-text-light)]" />
                    <p className="text-[var(--color-kids-text-muted)]">
                      No challenges found for this level. Try a different filter!
                    </p>
                  </CardContent>
                </Card>
              )}
            </section>

            {/* Stats Section */}
            {submissions.length > 0 && (
              <section>
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-display font-bold text-lg text-[var(--color-kids-text)] mb-4">
                      Your Progress
                    </h3>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-display font-bold text-[var(--color-kids-primary)]">
                          {submissions.filter(s => s.status === 'submitted').length}
                        </p>
                        <p className="text-sm text-[var(--color-kids-text-muted)]">
                          Completed
                        </p>
                      </div>
                      <div>
                        <p className="text-2xl font-display font-bold text-[var(--color-kids-secondary)]">
                          {submissions.reduce((sum, s) => sum + s.word_count, 0)}
                        </p>
                        <p className="text-sm text-[var(--color-kids-text-muted)]">
                          Words Written
                        </p>
                      </div>
                      <div>
                        <p className="text-2xl font-display font-bold text-[var(--color-kids-accent)]">
                          {new Set(submissions.filter(s => s.status === 'submitted').map(s => s.challenge_id)).size}
                        </p>
                        <p className="text-sm text-[var(--color-kids-text-muted)]">
                          Unique Challenges
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </section>
            )}
          </div>
        )}
      </main>
    </div>
  )
}

export default ChallengeHub
